import { css } from '@emotion/css';

/**
 * Transforms commission data into table format grouped by date
 * @param {Array} inputData - Raw commission data
 * @param {Array} employeeData - Employee data array for mapping IDs to names
 * @returns {Array} Transformed data grouped by date
 */
export const transformData = (inputData, employeeData = []) => {
  // Create employee ID to name mapping
  const employeeIdToNameMap = {};
  employeeData.forEach(emp => {
    employeeIdToNameMap[emp.id] = emp.name;
  });

  const rowData = [];

  inputData.forEach((item) => {
    // Map employee ID to employee name if available
    const employeeName = employeeIdToNameMap[item.employee_name] || item.employee_name;

    const existingDate = rowData.find((data) => data.date === item.date);

    if (existingDate) {
      const existingEmployee = existingDate.employee.find(emp => emp.name === employeeName);
      if (existingEmployee) {
        // Accumulate values for existing employee
        existingEmployee.handfee_times = (parseInt(existingEmployee.handfee_times) + parseInt(item.handfee_times || 0)).toString();
        existingEmployee.handfee_times_pms = (parseInt(existingEmployee.handfee_times_pms) + parseInt(item.handfee_times_pms || 0)).toString();
      } else {
        // Add new employee entry
        existingDate.employee.push({
          name: employeeName,
          handfee_times: item.handfee_times || "0",
          handfee_times_pms: item.handfee_times_pms || "0",
        });
      }
    } else {
      rowData.push({
        date: item.date,
        employee: [
          {
            name: employeeName,
            handfee_times: item.handfee_times || "0",
            handfee_times_pms: item.handfee_times_pms || "0",
          },
        ],
      });
    }
  });

  return rowData;
};

/**
 * Generates date range array between start and end dates
 * @param {string} start - Start date in YYYY-MM-DD format
 * @param {string} end - End date in YYYY-MM-DD format
 * @returns {Array} Array of formatted dates in DD/MM/YYYY format
 */
export const generateDateRange = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  let currentDate = startDate;
  const dates = [];

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates.map(date => {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // +1 because months are 0-indexed
    const year = date.getFullYear();
    return `${day}/${month}/${year}`; // format date as 'dd/mm/yyyy'
  });
};

/**
 * CSS styles for commission tables
 */
export const tableCSS = css({
  backgroundColor: 'white',
  '& table': {
    borderCollapse: 'collapse'
  },
  '& thead > tr': {
    borderWidth: '1px',
    borderColor: 'black',
    borderStyle: 'solid',
    backgroundColor: "blue"
  },
  '.ant-table-thead .ant-table-cell':
  {
    backgroundColor: '#00BA9E',
    color: 'white',
    borderRadius: 'unset !important'
  },
  "&& tbody > tr:hover > td": {
    background: "grey !important"
  }
});

/**
 * Processes employee data into a map for quick lookup
 * @param {Array} employeeData - Employee data array
 * @returns {Object} Map of employee names to employee objects
 */
export const createEmployeeDataMap = (employeeData) => {
  const employeeDataMap = {};
  employeeData?.forEach((item) => {
    employeeDataMap[item.name] = item;
  });
  return employeeDataMap;
};

/**
 * Creates table data from optimized data and employee list
 * @param {Array} optimizeData - Processed data with dates and employees
 * @param {Array} employeeData - Employee data array
 * @param {Object} employeeDataMap - Employee lookup map
 * @returns {Array} Formatted table data
 */
export const createTableData = (optimizeData, employeeData, employeeDataMap) => {
  return Array.isArray(optimizeData) ? optimizeData.map((row) => {
    const tableRow = {
      key: row.date,
      date: row.date,
    };

    employeeData?.forEach((item) => {
      tableRow[`${item.name}_regular`] = "0";
      tableRow[`${item.name}_pms`] = "0";
    });

    row.employee.forEach((employeeEntry) => {
      const employeeName = employeeEntry.name;
      const employeeData = employeeDataMap[employeeName];
      if (employeeData) {
        tableRow[`${employeeName}_regular`] = employeeEntry.handfee_times || "0";
        tableRow[`${employeeName}_pms`] = employeeEntry.handfee_times_pms || "0";
      }
    });

    return tableRow;
  }) : [];
};

/**
 * Creates summary rows for the table
 * @param {Array} tableData - Table data rows
 * @param {Array} employeeData - Employee data array
 * @returns {Object} Object containing summaryRow and calculatedSummaryRow
 */
export const createSummaryRows = (tableData, employeeData) => {
  const summaryRow = {
    key: 'summary',
    date: 'รวม(ครั้ง)',
  };

  const calculatedSummaryRow = {
    key: 'summaryCal',
    date: 'รวม(คำนวณ)'
  };

  employeeData?.forEach((item) => {
    // Sum regular commissions
    let sumRegular = 0;
    let sumPms = 0;

    tableData.forEach((row) => {
      sumRegular += parseInt(row[`${item.name}_regular`]) || 0;
      sumPms += parseInt(row[`${item.name}_pms`]) || 0;
    });

    // Total sum for summary
    const totalSum = sumRegular + sumPms;

    summaryRow[`${item.name}_regular`] = sumRegular.toString();
    summaryRow[`${item.name}_pms`] = sumPms.toString();

    // Use appropriate rate for calculations - pms_fee for PMS, handfee for regular
    const regularRate = parseFloat(item.handfee) || 0;
    const pmsRate = parseFloat(item.pms_fee) || parseFloat(item.handfee) || 0; // fallback to handfee if pms_fee not set

    calculatedSummaryRow[`${item.name}_regular`] = (sumRegular * regularRate).toString();
    calculatedSummaryRow[`${item.name}_pms`] = (sumPms * pmsRate).toString();
  });

  return { summaryRow, calculatedSummaryRow };
};
